/**
 * Governed form hooks. `useFmForm()` returns a SEALED domain handle exposing
 * only the operations a page legitimately needs — submit, reset, read/write
 * values, validate, and set a field error. The raw AntD `FormInstance` is kept
 * internal (bridged via a module-private WeakMap in `internal/fmFormBridge`), so
 * route/page code cannot reach `Form.useForm()`'s escape surface (getInternalHooks,
 * scrollToField, setFields with arbitrary field data, etc.).
 *
 * AntD stays an implementation detail of the DS form layer. Pages compose
 * `FmForm` (or the internal `FmFormShell`) and drive it through this handle;
 * they never import `antd`'s `Form`/`FormInstance`.
 */
import { useMemo } from 'react';
import { Form } from 'antd';
import type { NamePath } from 'antd/es/form/interface';
import { bindFmFormInstance, resolveFmFormInstance } from './internal/fmFormBridge';

/**
 * Sealed form handle. Exposes domain operations only — no raw AntD
 * `FormInstance`, no AntD types. Accepted by `FmForm`'s / `FmFormShell`'s
 * `form` prop and by `useFmFormWatch`.
 */
export interface FmFormHandle<Values = Record<string, unknown>> {
  /** Programmatically submit (runs validation, then the form's `onSubmit`). */
  submit(): void;
  /** Reset all fields (or a named subset) back to their initial values. */
  reset(fields?: Array<keyof Values>): void;
  /** Merge the given values into the current field values. */
  setValues(values: Partial<Values>): void;
  /** Set a single field's value. */
  setValue<K extends keyof Values>(name: K, value: Values[K]): void;
  /** Read the current field values. */
  getValues(): Values;
  /** Validate all fields; resolves with the values, rejects on failure. */
  validate(): Promise<Values>;
  /** Attach validation error message(s) to a single field. */
  setFieldError<K extends keyof Values>(name: K, errors: string[]): void;
}

/**
 * Create a sealed form handle. Drop-in for `const [form] = Form.useForm()`, but
 * `form` is a domain handle rather than the raw AntD instance.
 */
export function useFmForm<Values = Record<string, unknown>>(): [FmFormHandle<Values>] {
  const [instance] = Form.useForm<Values>();
  const handle = useMemo<FmFormHandle<Values>>(() => {
    const built: FmFormHandle<Values> = {
      submit: () => instance.submit(),
      reset: (fields) => instance.resetFields(fields as NamePath[] | undefined),
      setValues: (values) => instance.setFieldsValue(values),
      setValue: (name, value) => instance.setFieldValue(name as NamePath, value),
      getValues: () => instance.getFieldsValue(),
      validate: () => instance.validateFields(),
      setFieldError: (name, errors) => instance.setFields([{ name: name as NamePath, errors }]),
    };
    bindFmFormInstance(built, instance);
    return built;
  }, [instance]);
  return [handle];
}

/**
 * Subscribe to a single field's live value on a sealed handle. Domain
 * replacement for `Form.useWatch(name, form)` — the route never touches the raw
 * instance.
 */
export function useFmFormWatch<Values, K extends keyof Values & (string | number)>(
  name: K,
  handle: FmFormHandle<Values>,
): Values[K] | undefined {
  return Form.useWatch(name as NamePath, resolveFmFormInstance(handle)) as Values[K] | undefined;
}
